import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Share,
  Plus,
  Check,
  Copy,
  Smartphone,
  ArrowLeft,
  Compass,
  Info,
  Home,
  MoreVertical,
  Download,
  Apple,
} from "lucide-react";
import { toast } from "sonner";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

function detectPlatform() {
  if (typeof navigator === "undefined") return "ios";
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  return "ios";
}

const STEPS_IOS = [
  {
    icon: Compass,
    title: "Abra no Safari",
    desc: "Use o navegador Safari do iPhone e acesse o endereço do sistema. Só o Safari tem a opção de instalar — Chrome ou outros não funcionam no iPhone.",
    accent: "#249EEB",
  },
  {
    icon: Share,
    title: 'Toque em "Compartilhar"',
    desc: "Na barra do Safari, toque no ícone de Compartilhar (um quadrado com uma seta para cima ↥).",
    accent: "#7C3AED",
  },
  {
    icon: Plus,
    title: 'Escolha "Adicionar à Tela de Início"',
    desc: "Role a lista de opções e toque em \u201CAdicionar à Tela de Início\u201D (Add to Home Screen).",
    accent: "#EA9A3E",
  },
  {
    icon: Check,
    title: 'Confirme em "Adicionar"',
    desc: "Toque em \u201CAdicionar\u201D no canto superior direito. O ícone do Clean Manager vai aparecer na tela inicial.",
    accent: "#22C55E",
  },
  {
    icon: Home,
    title: "Abra pelo ícone",
    desc: "Pronto! Abra o app pelo ícone na tela inicial. Ele abre em tela cheia, mais rápido e funciona em campo.",
    accent: "#001340",
  },
];

const STEPS_ANDROID = [
  {
    icon: Compass,
    title: "Abra no Chrome",
    desc: "Use o navegador Chrome do Android e acesse o endereço do sistema.",
    accent: "#249EEB",
  },
  {
    icon: MoreVertical,
    title: "Toque no menu ⋮",
    desc: "Toque no menu de três pontinhos no canto superior direito do Chrome.",
    accent: "#7C3AED",
  },
  {
    icon: Download,
    title: 'Toque em "Instalar app"',
    desc: "Escolha \u201CInstalar app\u201D (ou \u201CAdicionar à tela inicial\u201D). Em alguns aparelhos aparece um aviso automático para instalar — é só tocar nele.",
    accent: "#EA9A3E",
  },
  {
    icon: Check,
    title: 'Confirme em "Instalar"',
    desc: "Toque em \u201CInstalar\u201D para confirmar. O ícone do Clean Manager vai aparecer na tela inicial.",
    accent: "#22C55E",
  },
  {
    icon: Home,
    title: "Abra pelo ícone",
    desc: "Pronto! Abra o app pelo ícone na tela inicial. Ele abre em tela cheia, mais rápido e funciona em campo.",
    accent: "#001340",
  },
];

export default function InstallMobile() {
  const location = useLocation();
  const forced = /iphone|ios/i.test(location.pathname)
    ? "ios"
    : /android/i.test(location.pathname)
      ? "android"
      : null;
  const [platform, setPlatform] = useState(() => forced || detectPlatform());
  const [copied, setCopied] = useState(false);

  const steps = platform === "android" ? STEPS_ANDROID : STEPS_IOS;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(ORIGIN);
      setCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Não foi possível copiar. Copie manualmente o endereço acima.");
    }
  }

  const browserName = platform === "android" ? "Chrome" : "Safari";

  return (
    <div
      className="min-h-screen w-full bg-[#F8FAFC] font-[Manrope]"
      data-testid="install-mobile-page"
    >
      {/* Header */}
      <div className="bg-[#001340] text-white">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link
            to="/login"
            data-testid="install-back-login"
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft size={16} /> Voltar ao login
          </Link>
          <div className="flex items-center gap-2">
            <img
              src="/assets/cleanmanager-emblem.png"
              alt="Clean Manager USA"
              className="h-7 w-7 object-contain"
              draggable={false}
            />
            <span className="font-[Outfit] font-bold text-sm">
              Clean Manager <span className="text-blue-300">USA</span>
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="text-center mb-6"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#249EEB]/12 text-[#249EEB] mb-4">
            <Smartphone size={30} />
          </div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Instalar no celular
          </h1>
          <p className="text-slate-500 mt-2 text-base">
            Em poucos toques o Clean Manager vira um app na sua tela inicial.
          </p>
        </motion.div>

        {/* Device toggle */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.04 }}
          data-testid="install-platform-toggle"
          className="grid grid-cols-2 gap-2 p-1 rounded-2xl bg-slate-100 mb-6 max-w-xs mx-auto"
        >
          <button
            type="button"
            data-testid="install-tab-ios"
            onClick={() => setPlatform("ios")}
            className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-semibold transition-all ${
              platform === "ios"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-900"
            }`}
          >
            <Apple size={16} /> iPhone
          </button>
          <button
            type="button"
            data-testid="install-tab-android"
            onClick={() => setPlatform("android")}
            className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-semibold transition-all ${
              platform === "android"
                ? "bg-white text-emerald-700 shadow-sm"
                : "text-slate-500 hover:text-slate-900"
            }`}
          >
            <Smartphone size={16} /> Android
          </button>
        </motion.div>

        {/* URL + copiar */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.06 }}
          className="mb-8 rounded-2xl border border-slate-200 bg-white p-4"
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
            Endereço para abrir no {browserName}
          </div>
          <div className="flex items-center gap-2">
            <div
              data-testid="install-url"
              className="flex-1 min-w-0 truncate font-mono text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5"
            >
              {ORIGIN}
            </div>
            <button
              type="button"
              onClick={copyLink}
              data-testid="install-copy-link"
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-colors"
            >
              {copied ? <Check size={15} /> : <Copy size={15} />}
              {copied ? "Copiado" : "Copiar"}
            </button>
          </div>
        </motion.div>

        {/* Passos */}
        <div className="space-y-3">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={`${platform}-${s.title}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.08 + i * 0.06 }}
                data-testid={`install-step-${i + 1}`}
                className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-4"
              >
                <div className="relative shrink-0">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: `${s.accent}1A`, color: s.accent }}
                  >
                    <Icon size={22} />
                  </div>
                  <span
                    className="absolute -top-2 -left-2 w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center shadow-sm"
                    style={{ background: s.accent }}
                  >
                    {i + 1}
                  </span>
                </div>
                <div className="min-w-0 pt-0.5">
                  <div className="font-semibold text-slate-900 leading-tight">
                    {s.title}
                  </div>
                  <div className="text-sm text-slate-500 mt-1 leading-snug">
                    {s.desc}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Aviso navegador */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="mt-6 flex items-start gap-2.5 rounded-2xl border border-amber-200 bg-amber-50 p-4"
        >
          <Info size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 leading-snug">
            {platform === "ios" ? (
              <>
                <strong>Importante:</strong> no iPhone, a instalação só aparece no{" "}
                <strong>Safari</strong>. Se você abriu pelo Chrome, Instagram ou
                WhatsApp, copie o endereço acima e cole no Safari primeiro.
              </>
            ) : (
              <>
                <strong>Dica:</strong> no Android use o <strong>Chrome</strong>.
                Se abriu pelo Instagram ou WhatsApp, toque no menu ⋮ e escolha
                "Abrir no Chrome", ou copie o endereço acima e cole no Chrome.
              </>
            )}
          </div>
        </motion.div>

        <div className="text-center mt-8">
          <Link
            to="/login"
            data-testid="install-goto-login"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#249EEB] hover:bg-[#1b86cc] text-white font-semibold transition-colors"
          >
            Ir para o login
          </Link>
        </div>
      </div>
    </div>
  );
}
